import React, { useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import axios from "axios";
import { useSelector } from "react-redux";
import HeaderTitle from "../../../components/common/HeaderTitle/HeaderTitle";
import DataTable from "../../../components/common/DataTable/DataTableSorting";
import SearchBar from "../../../components/common/SearchBar/SearchBar";
import { API_ENDPOINT } from "../../../services/api/resources";
import {
  DOCUMENT_UPLOAD_ACCEPT,
  DOCUMENT_UPLOAD_FORMAT_LABEL,
  isDocumentUploadFile,
} from "../../../utils/documentUploadTypes";
import sampleFilterDocumentExcel from "../../../assets/files/RC_Sample_Filter.xlsx";
import {
  getMatchedCatalogItems,
  normalizeFilteredCatalogResponse,
  normalizePriceCatalogItems,
} from "./priceListCatalogMapping";
import {
  enterpriseBackButtonSx,
  enterpriseBodyTextSx,
  enterpriseBrowseButtonSx,
  enterpriseDangerButtonSx,
  enterpriseGhostButtonSx,
  enterprisePageSx,
  enterprisePrimaryButtonSx,
  enterpriseSectionTitleSx,
  enterpriseSupportingTextSx,
  getEnterpriseUploadZoneSx,
} from "../../../components/common/upload/enterpriseUploadStyles";

const FILTER_UPLOAD_ACCEPT = ".csv,.xls,.xlsx";
const FILTER_UPLOAD_FORMAT_LABEL = "CSV, XLS, XLSX";
const FILTER_UPLOAD_EXTENSIONS = ["csv", "xls", "xlsx"];

/* =========================================================
   FILE VALIDATION
========================================================= */

const isFilterUploadFile = (file) => {
  const extension = String(file?.name || "")
    .split(".")
    .pop()
    ?.toLowerCase();

  return FILTER_UPLOAD_EXTENSIONS.includes(extension);
};

/* =========================================================
   NUMBER FORMAT HELPERS
========================================================= */

const toNumber = (value) => {
  if (value === null || value === undefined || value === "") {
    return 0;
  }

  const cleanedValue = String(value)
    .replace(/,/g, "")
    .replace(/%/g, "")
    .trim();

  const number = Number(cleanedValue);

  return Number.isNaN(number) ? 0 : number;
};

const formatAmount = (value) => {
  return toNumber(value).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

/* =========================================================
   DATE FORMAT
========================================================= */

const formatCreatedDate = (dateValue) => {
  // API currently sends created_date: ""
  // When empty, use today's date.
  if (!dateValue) {
    return new Date().toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  }

  const parsedDate = new Date(String(dateValue).replace(" ", "T"));

  if (Number.isNaN(parsedDate.getTime())) {
    return dateValue;
  }

  return parsedDate.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

/* =========================================================
   COMPANY TAX DISPLAY
========================================================= */

const getTaxDisplay = (tax = {}) => {
  const taxParts = [];

  if (Array.isArray(tax?.gst) && tax.gst.length > 0) {
    taxParts.push(`GST ${tax.gst.join(", ")}`);
  }

  if (Array.isArray(tax?.cgst) && tax.cgst.length > 0) {
    taxParts.push(`CGST ${tax.cgst.join(", ")}`);
  }

  if (Array.isArray(tax?.sgst) && tax.sgst.length > 0) {
    taxParts.push(`SGST ${tax.sgst.join(", ")}`);
  }

  if (Array.isArray(tax?.igst) && tax.igst.length > 0) {
    taxParts.push(`IGST ${tax.igst.join(", ")}`);
  }

  return taxParts.length > 0 ? taxParts.join(" | ") : "-";
};

/* =========================================================
   TABLE HEADINGS
========================================================= */

const getPreviewHeadings = () => [
  {
    label: "Sl No",
    field: "sl_no",
    col_width: "70px",
    noWrap: true,
    sticky: "left",
    stickyLeft: 0,
    stickyZIndex: 6,
  },
  {
    label: "Part No",
    field: "part_no",
    col_width: "130px",
    noWrap: true,
    sticky: "left",
    stickyLeft: "70px",
    stickyZIndex: 6,
  },
  {
    label: "Item Name",
    field: "item_name",
    col_width: "250px",
    cellType: "readMore",
    maxLength: 38,
  },
  {
    label: "Description",
    field: "description",
    col_width: "280px",
    cellType: "readMore",
    maxLength: 42,
  },
  {
    label: "UOM",
    field: "uom",
    col_width: "100px",
    noWrap: true,
  },
  {
    label: "Quantity",
    field: "quantity",
    col_width: "100px",
    noWrap: true,
  },
  {
    label: "Unit Price",
    field: "unit_price_before_tax",
    col_width: "130px",
    noWrap: true,
  },
  {
    label: "Tax %",
    field: "tax_percent",
    col_width: "100px",
    noWrap: true,
  },
  {
    label: "Total Price",
    field: "total_price",
    col_width: "140px",
    noWrap: true,
  },
  {
    label: "Currency",
    field: "currency",
    col_width: "100px",
    noWrap: true,
  },
];

/* =========================================================
   DETAIL POPUP FIELDS
========================================================= */

const detailFields = [
  ["Part No", "part_no"],
  ["Item Name", "item_name"],
  ["Description", "description"],
  ["Quantity", "quantity"],
  ["UOM", "uom"],
  ["Unit Price", "unit_price_before_tax"],
  ["Tax %", "tax_percent"],
  ["Price Without Tax", "price_without_tax"],
  ["Total Price", "total_price"],
  ["Currency", "currency"],
];

/* =========================================================
   COMPONENT
========================================================= */

const PriceListUploadPage = ({ onBack, onSave }) => {
  const loginData = useSelector((state) => state.loginData.value);

  const priceListInputRef = useRef(null);
  const filterInputRef = useRef(null);

  const [priceListFile, setPriceListFile] = useState(null);
  const [filterFile, setFilterFile] = useState(null);

  const [extractedData, setExtractedData] = useState(null);

  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  const [extractError, setExtractError] = useState("");
  const [saveError, setSaveError] = useState("");

  const [showPriceListPreview, setShowPriceListPreview] =
    useState(false);

  const [showFilterPreview, setShowFilterPreview] =
    useState(false);

  const [resultSearch, setResultSearch] = useState("");

  const [selectedDetailRow, setSelectedDetailRow] =
    useState(null);

  const [savedRecord, setSavedRecord] = useState(null);

  /* =========================================================
     COMPANY DETAILS
  ========================================================= */

  const companyDetails = extractedData?.company_details || {};

  const companyName =
    companyDetails?.company_name || "-";

  const companyGst =
    companyDetails?.gst_number || "-";

  const companyPan =
    companyDetails?.pan_number || "-";

  const companyLocation =
    companyDetails?.location || "-";

  const warranty =
    companyDetails?.warranty || "-";

  const companyTax =
    companyDetails?.tax || {};

  const taxDisplay =
    getTaxDisplay(companyTax);

  /* =========================================================
     TOTALS - DIRECT API MAPPING
  ========================================================= */

  const subtotal =
    extractedData?.matched_results?.total_price_without_tax ||
    "0.00";

  const grandTotal =
    extractedData?.matched_results?.grand_total ||
    "0.00";

  const quotationDate =
    formatCreatedDate(extractedData?.created_date);

  /* =========================================================
     EXTRACT MATCHED ITEMS

     matched_results
        -> filter_results
            -> matched_items
  ========================================================= */

  const extractedCatalogRows = useMemo(() => {
    const filterResults =
      extractedData?.matched_results?.filter_results || [];

    const matchedItems = filterResults.flatMap(
      (filter) =>
        filter?.matched_items?.map((item) => ({
          ...item,

          filter_id: filter?.filter_id,

          filter_name:
            filter?.name || "",

          source_requirement:
            filter?.source_requirement || "",
        })) || [],
    );

    return matchedItems.map((item, index) => ({
      ...item,

      sl_no: index + 1,

      part_no:
        item?.item_code ||
        item?.part_no ||
        item?.item_id ||
        "-",

      item_name:
        item?.item_name || "-",

      description:
        item?.description || "-",

      uom:
        item?.uom || "-",

      quantity:
        item?.quantity || "0",

      /*
       * DIRECTLY FROM:
       * matched_items[].unit_price
       */
      unit_price_before_tax:
        formatAmount(item?.unit_price),

      /*
       * DIRECTLY FROM:
       * matched_items[].tax
       */
      tax_percent:
        item?.tax || "-",

      /*
       * DIRECTLY FROM:
       * matched_items[].price_without_tax
       */
      price_without_tax:
        formatAmount(item?.price_without_tax),

      /*
       * DIRECTLY FROM:
       * matched_items[].total_price
       */
      total_price:
        formatAmount(item?.total_price),

      currency:
        item?.currency || "INR",

      row_number:
        item?.row_number || "-",
    }));
  }, [extractedData]);

  /* =========================================================
     TABLE HEADINGS
  ========================================================= */

  const extractedCatalogHeadings = useMemo(
    () => getPreviewHeadings(),
    [],
  );

  /* =========================================================
     SEARCH FILTER
  ========================================================= */

  const filteredExtractedCatalogRows = useMemo(() => {
    const searchValue =
      resultSearch.trim().toLowerCase();

    if (!searchValue) {
      return extractedCatalogRows;
    }

    return extractedCatalogRows.filter((row) =>
      extractedCatalogHeadings.some((heading) =>
        String(row?.[heading.field] || "")
          .toLowerCase()
          .includes(searchValue),
      ),
    );
  }, [
    extractedCatalogHeadings,
    extractedCatalogRows,
    resultSearch,
  ]);

  /* =========================================================
     CURRENCY
  ========================================================= */

  const currency = useMemo(() => {
    const item = extractedCatalogRows.find(
      (row) => row?.currency,
    );

    return item?.currency || "INR";
  }, [extractedCatalogRows]);

  /* =========================================================
     AUTH TOKENS
  ========================================================= */

  const organizationToken =
    loginData?.organization_token ||
    loginData?.org_token ||
    loginData?.user?.organization_token ||
    loginData?.user?.org_token ||
    "";

  const authToken =
    loginData?.auth_token ||
    loginData?.access_token ||
    loginData?.token ||
    loginData?.user?.auth_token ||
    "";

  /* =========================================================
     DRAG & DROP
  ========================================================= */

  const handleDrop = (e, type) => {
    e.preventDefault();

    const file =
      e.dataTransfer.files?.[0];

    if (!file) return;

    const invalidFile =
      type === "filter"
        ? !isFilterUploadFile(file)
        : !isDocumentUploadFile(file);

    if (invalidFile) {
      return;
    }

    if (type === "priceList") {
      setPriceListFile(file);
      setShowPriceListPreview(false);
    } else {
      setFilterFile(file);
      setShowFilterPreview(false);
    }

    setExtractedData(null);
    setSavedRecord(null);
    setExtractError("");
    setSaveError("");
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  /* =========================================================
     FILE CHANGE
  ========================================================= */

  const handleFileChange = (e, type) => {
    const file =
      e.target.files?.[0];

    if (!file) return;

    const invalidFile =
      type === "filter"
        ? !isFilterUploadFile(file)
        : !isDocumentUploadFile(file);

    if (invalidFile) {
      return;
    }

    if (type === "priceList") {
      setPriceListFile(file);
      setShowPriceListPreview(false);
    } else {
      setFilterFile(file);
      setShowFilterPreview(false);
    }

    setExtractedData(null);
    setSavedRecord(null);
    setExtractError("");
    setSaveError("");
  };

  /* =========================================================
     CLEAR FILE
  ========================================================= */

  const handleClearFile = (type) => {
    if (type === "priceList") {
      setPriceListFile(null);

      setShowPriceListPreview(false);

      if (priceListInputRef.current) {
        priceListInputRef.current.value = "";
      }
    } else {
      setFilterFile(null);

      setShowFilterPreview(false);

      if (filterInputRef.current) {
        filterInputRef.current.value = "";
      }
    }

    setExtractedData(null);
    setSavedRecord(null);

    setExtractError("");
    setSaveError("");
  };

  /* =========================================================
     SAVE FILTERED CATALOG
  ========================================================= */

  const saveFilteredCatalog = async (record) => {
    if (!record) {
      return null;
    }

    if (!organizationToken) {
      setSaveError(
        "Organization token not found. Please login again.",
      );

      return null;
    }

    const headers = {
      "Content-Type": "application/json",
    };

    if (authToken) {
      headers.Authorization =
        `Bearer ${authToken}`;
    }

    const requestBody = {
      organization_token: organizationToken,

      filter_result:
        record.raw_filter_response ||
        record,
    };

    const postSaveRequest = (
      requestHeaders,
    ) =>
      axios.post(
        API_ENDPOINT.save_price_catalog_filter,
        requestBody,
        {
          headers: requestHeaders,
        },
      );

    let response;

    try {
      response =
        await postSaveRequest(headers);
    } catch (error) {
      const shouldRetryWithTokenAuth =
        authToken &&
        (error?.response?.status === 401 ||
          error?.response?.status === 403);

      if (!shouldRetryWithTokenAuth) {
        throw error;
      }

      response =
        await postSaveRequest({
          ...headers,

          Authorization:
            `Token ${authToken}`,
        });
    }

    const payload =
      response?.data || {};

    if (
      payload?.status === false ||
      payload?.success === false
    ) {
      setSaveError(
        payload?.message ||
          "Unable to save filtered catalog.",
      );

      return null;
    }

    const savedMatchedItems =
      normalizePriceCatalogItems(
        getMatchedCatalogItems(payload),
      );

    const preservedItems =
      savedMatchedItems.length > 0
        ? savedMatchedItems
        : record.line_items;

    const nextSavedRecord = {
      ...record,
      ...payload,

      document_number:
        record.document_number,

      buyer_name:
        record.buyer_name,

      vendor_name:
        record.vendor_name,

      created_date:
        record.created_date,

      matched_count:
        payload?.matched_count ??
        record.matched_count,

      line_items:
        preservedItems,

      raw_filter_response:
        record.raw_filter_response,

      save_response:
        payload,
    };

    return nextSavedRecord;
  };

  /* =========================================================
     GENERATE QUOTATION
  ========================================================= */

  const handleExtract = async () => {
    if (
      !priceListFile ||
      !filterFile
    ) {
      return;
    }

    try {
      setLoading(true);

      setExtractError("");
      setSaveError("");

      setSavedRecord(null);

      const formData =
        new FormData();

      formData.append(
        "filter_document",
        filterFile,
      );

      formData.append(
        "price_catalog",
        priceListFile,
      );

      const response =
        await axios.post(
          API_ENDPOINT.filter_price_catalog,
          formData,
          {
            headers: {
              "Content-Type":
                "multipart/form-data",
            },
          },
        );

      const payload =
        response?.data || {};

      if (
        payload?.status === false ||
        payload?.success === false
      ) {
        setExtractError(
          payload?.message ||
            "Unable to generate quotation.",
        );

        setExtractedData(null);

        return;
      }

      /*
       * Expected API:
       *
       * {
       *   status: true,
       *   data: {
       *      company_details,
       *      matched_results
       *   }
       * }
       */

      const normalizedResponse =
        normalizeFilteredCatalogResponse(
          payload,
          priceListFile,
          filterFile,
        );

      setExtractedData(
        normalizedResponse,
      );

      setResultSearch("");
    } catch (error) {
      console.error(
        "Error filtering price catalog:",
        error,
      );

      setExtractError(
        error?.response?.data?.message ||
          error?.response?.data?.response ||
          "Unable to generate quotation. Please try again.",
      );

      setExtractedData(null);
    } finally {
      setLoading(false);
    }
  };

  /* =========================================================
     SAVE
  ========================================================= */

  const handleSave = async () => {
    if (
      !extractedData ||
      savedRecord
    ) {
      return;
    }

    try {
      setSaveLoading(true);

      setSaveError("");

      const saved =
        await saveFilteredCatalog(
          extractedData,
        );

      if (saved) {
        setSavedRecord(saved);

        if (onSave) {
          onSave(saved);
        }
      }
    } catch (error) {
      console.error(
        "Error saving filtered price catalog:",
        error,
      );

      setSaveError(
        error?.response?.data?.message ||
          error?.response?.data?.response ||
          "Unable to save filtered catalog. Please try again.",
      );
    } finally {
      setSaveLoading(false);
    }
  };

  /* =========================================================
     UI
  ========================================================= */

  return (
    <Box sx={enterprisePageSx}>
      {/* =====================================================
          PAGE HEADER
      ===================================================== */}

      <Box
        sx={{
          mt: 1,
          mb: 2,
          ml: -1,
          display: "flex",

          justifyContent:
            "space-between",

          alignItems:
            "center",

          flexWrap:
            "wrap",
        }}
      >
        <HeaderTitle
          titleText="Create Quotation"
        />

        <Button
          variant="contained"
          size="small"
          sx={enterpriseBackButtonSx}
          onClick={onBack}
        >
          Back
        </Button>
      </Box>

      {/* =====================================================
          UPLOAD SECTION
      ===================================================== */}

      <Box
        sx={{
          display: "flex",

          flexWrap: "wrap",

          gap: 4,

          justifyContent:
            "space-between",
        }}
      >
        {/* PRICE LIST */}

        <Box
          sx={{
            flex: "1 1 420px",

            display: "flex",

            flexDirection:
              "column",

            gap: 2,
          }}
        >
          <Typography sx={enterpriseSectionTitleSx}>
            Upload Price List
          </Typography>

          <Box
            onDrop={(e) =>
              handleDrop(
                e,
                "priceList",
              )
            }
            onDragOver={
              handleDragOver
            }
            sx={getEnterpriseUploadZoneSx(false)}
          >
            <Typography sx={{ ...enterpriseBodyTextSx, mb: 0.5, wordBreak: "break-all" }}>
              {priceListFile
                ? priceListFile.name
                : "Drag and drop file here"}
            </Typography>

            <Typography sx={enterpriseSupportingTextSx}>
              (Supported:{" "}
              {
                DOCUMENT_UPLOAD_FORMAT_LABEL
              }
              )
            </Typography>

            <Box
              sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}
            >
              <Button
                variant="contained"
                size="medium"
                sx={enterpriseBrowseButtonSx}
                onClick={() =>
                  priceListInputRef.current?.click()
                }
              >
                Browse
              </Button>

              {priceListFile && (
                <>
                  <Button
                    variant="outlined"
                    size="medium"
                    sx={enterpriseGhostButtonSx}
                    onClick={() =>
                      setShowPriceListPreview(
                        (prev) =>
                          !prev,
                      )
                    }
                  >
                    {showPriceListPreview
                      ? "Hide Preview"
                      : "Preview"}
                  </Button>

                  <Button
                    variant="outlined"
                    size="medium"
                    sx={enterpriseDangerButtonSx}
                    onClick={() =>
                      handleClearFile(
                        "priceList",
                      )
                    }
                  >
                    Clear
                  </Button>
                </>
              )}
            </Box>
          </Box>
        </Box>

        {/* FILTER FILE */}

        <Box
          sx={{
            flex:
              "1 1 420px",

            display:
              "flex",

            flexDirection:
              "column",

            gap: 2,
          }}
        >
          <Typography sx={enterpriseSectionTitleSx}>
            Filter Record
          </Typography>

          <Box
            onDrop={(e) =>
              handleDrop(
                e,
                "filter",
              )
            }
            onDragOver={
              handleDragOver
            }
            sx={getEnterpriseUploadZoneSx(false)}
          >
            <Typography sx={{ ...enterpriseBodyTextSx, mb: 0.5, wordBreak: "break-all" }}>
              {filterFile
                ? filterFile.name
                : "Drag and drop file here"}
            </Typography>

            <Typography sx={enterpriseSupportingTextSx}>
              (Supported:{" "}
              {
                FILTER_UPLOAD_FORMAT_LABEL
              }
              )
            </Typography>

            <Box
              sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}
            >
              <Button
                variant="contained"
                size="medium"
                sx={enterpriseBrowseButtonSx}
                onClick={() =>
                  filterInputRef.current?.click()
                }
              >
                Browse
              </Button>

              {filterFile && (
                <>
                  <Button
                    variant="outlined"
                    size="medium"
                    sx={enterpriseGhostButtonSx}
                    onClick={() =>
                      setShowFilterPreview(
                        (prev) =>
                          !prev,
                      )
                    }
                  >
                    {showFilterPreview
                      ? "Hide Preview"
                      : "Preview"}
                  </Button>

                  <Button
                    variant="outlined"
                    size="medium"
                    sx={enterpriseDangerButtonSx}
                    onClick={() =>
                      handleClearFile(
                        "filter",
                      )
                    }
                  >
                    Clear
                  </Button>
                </>
              )}
            </Box>
          </Box>

          {sampleFilterDocumentExcel && (
            <a
              href={
                sampleFilterDocumentExcel
              }
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color:
                  "#0000ffac",

                fontSize:
                  "1.4rem",

                marginTop:
                  "1rem",

                cursor:
                  "pointer",

                fontFamily:
                  "GilroyMedium",

                textDecoration:
                  "underline",

                display:
                  "inline-block",
              }}
            >
              Sample Filter CSV
            </a>
          )}
        </Box>
      </Box>

      {/* =====================================================
          HIDDEN INPUTS
      ===================================================== */}

      <input
        type="file"
        accept={
          DOCUMENT_UPLOAD_ACCEPT
        }
        ref={
          priceListInputRef
        }
        style={{
          display:
            "none",
        }}
        onChange={(e) =>
          handleFileChange(
            e,
            "priceList",
          )
        }
      />

      <input
        type="file"
        accept={
          FILTER_UPLOAD_ACCEPT
        }
        ref={
          filterInputRef
        }
        style={{
          display:
            "none",
        }}
        onChange={(e) =>
          handleFileChange(
            e,
            "filter",
          )
        }
      />

      {/* =====================================================
          PRICE LIST PREVIEW
      ===================================================== */}

      {showPriceListPreview &&
        priceListFile && (
          <Box
            sx={{
              mt: 2,

              p: 1,

              borderRadius:
                2,

              border:
                "1px solid #e2e8f0",

              backgroundColor:
                "white",
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                color:
                  "#075b97",

                fontWeight:
                  600,

                mb: 1,

                fontSize:
                  14,

                textAlign:
                  "left",
              }}
            >
              Price List Preview:
            </Typography>

            <iframe
              src={URL.createObjectURL(
                priceListFile,
              )}
              title="Price List Preview"
              width="100%"
              height="250px"
              style={{
                borderRadius:
                  "8px",

                border:
                  "1px solid #e2e8f0",
              }}
            />
          </Box>
        )}

      {/* =====================================================
          ACTION BUTTONS
      ===================================================== */}

      <Box
        sx={{
          display:
            "flex",

          gap: 2,

          mt: 3,
        }}
      >
        <Button
          variant="contained"
          size="small"
          sx={{
            ...enterprisePrimaryButtonSx,
            opacity:
              !priceListFile ||
              !filterFile ||
              loading ||
              saveLoading
                ? 0.7
                : 1,
          }}
          onClick={
            handleExtract
          }
          disabled={
            !priceListFile ||
            !filterFile ||
            loading ||
            saveLoading ||
            Boolean(
              extractedData,
            )
          }
        >
          {loading ? (
            <CircularProgress
              size={16}
              color="inherit"
            />
          ) : extractedData ? (
            "Quotation Generated"
          ) : (
            "Generate Quotation"
          )}
        </Button>

        <Button
          variant="contained"
          size="small"
          sx={{
            ...enterprisePrimaryButtonSx,
            opacity:
              !extractedData ||
              loading ||
              saveLoading ||
              savedRecord
                ? 0.7
                : 1,
          }}
          onClick={
            handleSave
          }
          disabled={
            !extractedData ||
            loading ||
            saveLoading ||
            Boolean(
              savedRecord,
            )
          }
        >
          {saveLoading ? (
            <CircularProgress
              size={16}
              color="inherit"
            />
          ) : savedRecord ? (
            "Saved"
          ) : (
            "Save"
          )}
        </Button>
      </Box>

      {/* =====================================================
          ERRORS
      ===================================================== */}

      {extractError && (
        <Typography
          sx={{
            color:
              "#b91c1c",

            fontSize:
              14,

            mt: 1,
          }}
        >
          {extractError}
        </Typography>
      )}

      {saveError && (
        <Typography
          sx={{
            color:
              "#b91c1c",

            fontSize:
              14,

            mt: 1,
          }}
        >
          {saveError}
        </Typography>
      )}

      {/* =====================================================
          GENERATED QUOTATION
      ===================================================== */}

      {extractedData && (
        <Box sx={{ mt: 3 }}>
          {/* ===============================================
              COMPANY DETAILS
          =============================================== */}

          <Box
            sx={{
              mb: 3,

              p: 2,

              borderRadius:
                2,

              border:
                "1px solid #e2e8f0",

              backgroundColor:
                "#f8fafc",

              display:
                "grid",

              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr 1fr",
              },

              gap: 1.5,
            }}
          >
            {/* COMPANY NAME */}

            <Typography
              sx={{
                fontFamily:
                  "GilroySemiBold",

                fontSize:
                  "15px",
              }}
            >
              Company Name:{" "}
              <Box
                component="span"
                sx={{
                  fontFamily:
                    "GilroyMedium",
                }}
              >
                {companyName}
              </Box>
            </Typography>

            {/* LOCATION */}

            <Typography
              sx={{
                fontFamily:
                  "GilroySemiBold",

                fontSize:
                  "15px",
              }}
            >
              Location:{" "}
              <Box
                component="span"
                sx={{
                  fontFamily:
                    "GilroyMedium",
                }}
              >
                {
                  companyLocation
                }
              </Box>
            </Typography>

            {/* GST */}

            <Typography
              sx={{
                fontFamily:
                  "GilroySemiBold",

                fontSize:
                  "15px",
              }}
            >
              GSTIN:{" "}
              <Box
                component="span"
                sx={{
                  fontFamily:
                    "GilroyMedium",
                }}
              >
                {companyGst}
              </Box>
            </Typography>

            {/* PAN */}

            <Typography
              sx={{
                fontFamily:
                  "GilroySemiBold",

                fontSize:
                  "15px",
              }}
            >
              PAN:{" "}
              <Box
                component="span"
                sx={{
                  fontFamily:
                    "GilroyMedium",
                }}
              >
                {companyPan}
              </Box>
            </Typography>

            {/* DATE */}

            <Typography
              sx={{
                fontFamily:
                  "GilroySemiBold",

                fontSize:
                  "15px",
              }}
            >
              Date:{" "}
              <Box
                component="span"
                sx={{
                  fontFamily:
                    "GilroyMedium",
                }}
              >
                {
                  quotationDate
                }
              </Box>
            </Typography>
          </Box>

          {/* ===============================================
              SEARCH
          =============================================== */}

          <Box
            sx={{
              flex: 1,
              mb: 2,
            }}
          >
            <SearchBar
              value={
                resultSearch
              }
              onChange={
                setResultSearch
              }
              placeholder="Search extracted catalog"
            />
          </Box>

          {/* ===============================================
              MATCHED ITEMS TABLE
          =============================================== */}

          <DataTable
            headings={
              extractedCatalogHeadings
            }
            tableData={
              filteredExtractedCatalogRows
            }
            tableSize="220rem"
            fixedLayout
            stickyHead
            isLoading={
              loading
            }
            errorMessage={
              extractedCatalogRows.length ===
              0
                ? "No matched catalog items found."
                : filteredExtractedCatalogRows.length ===
                    0
                  ? "No matching catalog records found."
                  : ""
            }
            sorting={
              true
            }
          />

          {/* ===============================================
              WARRANTY + TOTAL SUMMARY
          =============================================== */}

          <Box
            sx={{
              mt: 3,

              display:
                "flex",

              flexDirection: {
                xs: "column",
                md: "row",
              },

              justifyContent:
                "space-between",

              alignItems: {
                xs: "stretch",
                md: "flex-start",
              },

              gap: 2,
            }}
          >
            {/* =============================================
                WARRANTY LEFT
            ============================================= */}

            <Box
              sx={{
                flex: 1,

                p: 2,

                borderRadius:
                  2,

                border:
                  "1px solid #e2e8f0",

                backgroundColor:
                  "#f8fafc",

                maxWidth: {
                  xs: "100%",
                  md: "55%",
                },

                minHeight:
                  "105px",
              }}
            >
              <Typography
                sx={{
                  fontFamily:
                    "GilroySemiBold",

                  fontSize:
                    "15px",

                  color:
                    "#1e293b",

                  mb: 1,
                }}
              >
                Warranty
              </Typography>

              <Typography
                sx={{
                  fontFamily:
                    "GilroyMedium",

                  fontSize:
                    "14px",

                  color:
                    "#475569",

                  lineHeight:
                    1.6,
                }}
              >
                {warranty}
              </Typography>
            </Box>

            {/* =============================================
                PRICE SUMMARY RIGHT
            ============================================= */}

            <Box
              sx={{
                p: 2,

                borderRadius:
                  2,

                backgroundColor:
                  "#f0f7fa",

                border:
                  "1px solid #cbd5e1",

                display:
                  "flex",

                flexDirection:
                  "column",

                gap: 1.2,

                width: {
                  xs: "100%",
                  md: "400px",
                },

                boxSizing:
                  "border-box",
              }}
            >
              {/* ===========================================
                  SUBTOTAL WITHOUT TAX
              =========================================== */}

              <Box
                sx={{
                  display:
                    "flex",

                  justifyContent:
                    "space-between",

                  alignItems:
                    "center",

                  gap: 2,
                }}
              >
                <Typography
                  sx={{
                    fontFamily:
                      "GilroyMedium",

                    fontSize:
                      "14px",
                  }}
                >
                  Subtotal
                  (Excl. Tax)
                </Typography>

                <Typography
                  sx={{
                    fontFamily:
                      "GilroySemiBold",

                    fontSize:
                      "14px",

                    whiteSpace:
                      "nowrap",
                  }}
                >
                  {currency}{" "}
                  {
                    formatAmount(
                      subtotal,
                    )
                  }
                </Typography>
              </Box>

              {/* ===========================================
                  TAX
              =========================================== */}

              <Box
                sx={{
                  display:
                    "flex",

                  justifyContent:
                    "space-between",

                  alignItems:
                    "center",

                  gap: 2,
                }}
              >
                <Typography
                  sx={{
                    fontFamily:
                      "GilroyMedium",

                    fontSize:
                      "14px",
                  }}
                >
                  Tax
                </Typography>

                <Typography
                  sx={{
                    fontFamily:
                      "GilroySemiBold",

                    fontSize:
                      "14px",

                    textAlign:
                      "right",
                  }}
                >
                  {
                    taxDisplay
                  }
                </Typography>
              </Box>

              {/* DIVIDER */}

              <Box
                sx={{
                  borderTop:
                    "1px solid #cbd5e1",

                  my: 0.5,
                }}
              />

              {/* ===========================================
                  GRAND TOTAL INCLUDING TAX
              =========================================== */}

              <Box
                sx={{
                  display:
                    "flex",

                  justifyContent:
                    "space-between",

                  alignItems:
                    "center",

                  gap: 2,
                }}
              >
                <Typography
                  sx={{
                    fontFamily:
                      "GilroySemiBold",

                    fontSize:
                      "16px",

                    color:
                      "#075b97",
                  }}
                >
                  Grand Total
                </Typography>

                <Typography
                  sx={{
                    fontFamily:
                      "GilroySemiBold",

                    fontSize:
                      "18px",

                    color:
                      "#075b97",

                    whiteSpace:
                      "nowrap",
                  }}
                >
                  {currency}{" "}
                  {
                    formatAmount(
                      grandTotal,
                    )
                  }
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      {/* =====================================================
          ITEM DETAILS DIALOG
      ===================================================== */}

      <Dialog
        open={Boolean(
          selectedDetailRow,
        )}
        onClose={() =>
          setSelectedDetailRow(
            null,
          )
        }
        fullWidth
        maxWidth="md"
      >
        <DialogTitle
          sx={{
            fontSize:
              "18px",

            fontWeight:
              600,

            fontFamily:
              "GilroySemiBold",
          }}
        >
          Catalog Item Details
        </DialogTitle>

        <DialogContent>
          <Box
            sx={{
              display:
                "grid",

              gridTemplateColumns:
                "repeat(auto-fit, minmax(220px, 1fr))",

              gap: 1.5,

              py: 1,
            }}
          >
            {detailFields.map(
              ([label, field]) => (
                <Box
                  key={
                    field
                  }
                  sx={{
                    border:
                      "1px solid #e2e8f0",

                    borderRadius:
                      1,

                    p: 1.2,

                    backgroundColor:
                      "#f8fafc",
                  }}
                >
                  <Typography
                    sx={{
                      color:
                        "#64748b",

                      fontSize:
                        12,

                      fontWeight:
                        600,
                    }}
                  >
                    {label}
                  </Typography>

                  <Typography
                    sx={{
                      color:
                        "#0f172a",

                      fontSize:
                        14,

                      fontFamily:
                        "GilroyMedium",

                      wordBreak:
                        "break-word",
                    }}
                  >
                    {selectedDetailRow?.[
                      field
                    ] || "-"}
                  </Typography>
                </Box>
              ),
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default PriceListUploadPage;
